import { AfterViewInit, Component, Inject, LOCALE_ID, OnDestroy, ViewChild } from "@angular/core";
import { OrganisationService } from "../../organisation/organisation.service";
import { ActivatedRoute, Router } from "@angular/router";
import { LinqsetRepositoryService } from "../../model-services/linqset-repository.service";
import { GraphComponentComponent } from "../../canvas/graph-component/graph-component.component";
import { ACTION_TYPE, getTotalModelCost, MODEL_FUNCTION, ModelDetails, ModelInsights, ORGANISATION_STATE, Organisation, Project } from "../../common/organisation";
import { DEFAULT_URL, MODEL_WALKTHROUGH_URL, SKETCH_URL } from "../../app-urls";
import { UserService } from "../../user/user.service";
import { CURRENT_STATE_PROJECT_KEY, Linqset, MODEL_TYPE, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from "../../common/linqset-model";
import { MiniGraphType } from "../../canvas/mini-graph/mini-graph.component";
import { FUNCTION, FunctionService } from "../../model-services/function.service";
import { USER_ROLE, User } from "../../common/user";
import { ModelService } from "../../organisation/models/model-service";
import { UserDialogComponent, UserDialogData } from "../../user/user-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { OrganisationUsersComponent } from "../../organisation/users/organisation-users.component";
import { Subscription } from "rxjs";
import { TicketListComponent } from "../../user/support/ticket-list.component";
import { AppComponent } from "../../app.component";
import { SCREEN } from "../layouts-enums";
import { GraphService } from "../../canvas/graph.service";
import { OnboardingService } from "../../user/onboarding.service";
import { ONBOARDING_CHOICE } from "../../user/onboarding";
import { ConfirmDialogComponent, ConfirmDialogModel } from "../../ui-components/dialogs/confirm-dialog.component";
import { BackupService } from "../../organisation/backups/backup.service";
import { OrganisationTransferService } from "../../organisation/organisation-transfer.service";
import { formatDate } from "@angular/common";
import { GraphComponent, IEdge, IModelItem, INode } from "@yfiles/yfiles";
import { InsightsOverviewComponent } from "../../insights/insights-overview-component";
import { FormattingService } from "../../linq-utils/formatting/formatting.service";
import { ModelChangeDialogComponent } from "../../ai/model-changes/model-change-dialog.component";
import { ScopeService, wholeOrganisation } from "../../analysis/scope";
import { Change, changeFormat, changeFrom, measure, Measure } from "../../analysis/comparison";

const COMPONENT = "Organisation layout"

interface HeadlineMetric {
    label:string
    value:string
    short:string        // the value with thousands as K and millions as M, shown when the value doesn't fit
    tooltip:string
    change?:Change      // against current state, only when viewing a project
    before?:string      // the current state's figure
    warn?:boolean
}

@Component({
    selector: 'app-organisation-layout',
    templateUrl: './organisation-layout.html',
    styleUrls: ['./organisation-layout.scss'],
    standalone: false
})
export class OrganisationLayoutComponent implements AfterViewInit, OnDestroy {
    screen:SCREEN = SCREEN.ORGANISATION_HOME

    linqset: Linqset
    insights:ModelInsights
    comparison:Linqset

    loading

    workspaceName:string
    headlines:HeadlineMetric[] = []
    todoCount:number|undefined      // reported by the child lists once loaded; used to shrink empty cards
    ticketCount:number|undefined

    organisationKey:string
    organisation:Organisation
    projectKey:string
    project:Project   
    helpGuides:string[] = []        // the help guides about this screen

    graphSelection:any // flag used to indicate a change in graph selection. 
    selectedItem:IModelItem = null
    modelType = MODEL_TYPE.MODELS
    routeSub:Subscription
    updatedOrganisationSub:Subscription
    linqsetLoadedSub:Subscription
    ticketsReloadSub:Subscription
    
    ORGANISATION_STATE=ORGANISATION_STATE

    @ViewChild(GraphComponentComponent) gcComponent: GraphComponentComponent
    @ViewChild(InsightsOverviewComponent) insightsComponent: InsightsOverviewComponent
    @ViewChild(OrganisationUsersComponent) usersComponent: OrganisationUsersComponent
    @ViewChild(TicketListComponent) ticketsComponent: TicketListComponent

    graphComponent!: GraphComponent
    MiniGraphType = MiniGraphType
    FUNCTION=FUNCTION

    constructor(public organisationService:OrganisationService, private router: Router, private modelService:ModelService, private dialog:MatDialog,
        public linqsetRepo: LinqsetRepositoryService, private route:ActivatedRoute, private functionService:FunctionService, public app:AppComponent,
        private userService:UserService, private graphService:GraphService, 
        private onboardingService:OnboardingService, private backupService:BackupService, private transferService:OrganisationTransferService, private formatter:FormattingService, private scopeService:ScopeService, @Inject(LOCALE_ID) public locale: string) {
            this.helpGuides = ['organisations-and-users', 'models', 'projects']            
    }

    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
        if (this.updatedOrganisationSub) this.updatedOrganisationSub.unsubscribe()
        if (this.linqsetLoadedSub) this.linqsetLoadedSub.unsubscribe()
        if (this.ticketsReloadSub) this.ticketsReloadSub.unsubscribe()
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.routeSub = this.route.params.subscribe(async p => {
                this.organisationKey = p['organisation_key']
                this.projectKey = p['project_key']

                // coming home starts afresh: the analysis screens look at every model again, in the workspace chosen here
                const workspaceKey = this.projectKey || CURRENT_STATE_PROJECT_KEY
                const scope = this.scopeService.open(this.organisationKey)
                if (scope.workspaceKey !== workspaceKey || scope.modelKeys.length || scope.modelFunctions.length || scope.modelTags.length)
                    this.scopeService.set(wholeOrganisation(workspaceKey))

                this.loadOrganisationData()
            })
        })

        this.updatedOrganisationSub = this.organisationService.updatedOrganisationEvent.subscribe(e => {
            this.loadOrganisationData()
        })

        this.linqsetLoadedSub = this.functionService.linqsetLoadedEvent.subscribe(e => {
            this.loadOrganisationData()
        })

        this.ticketsReloadSub = this.functionService.reloadTicketsEvent.subscribe(e => {
            this.ticketsComponent.refreshTickets()
        })
    }

    async loadOrganisationData() {
        this.loading = true

        try {           
            if (!this.organisationKey) return            

            this.linqset = await this.linqsetRepo.loadOrganisationHomeData(this.organisationKey, this.projectKey||CURRENT_STATE_PROJECT_KEY)

            this.organisation = this.organisationService.organisation
            this.project = this.projectKey && this.projectKey !== CURRENT_STATE_PROJECT_KEY && this.organisation.projects.find(p => p._key === this.projectKey)
            this.workspaceName = this.project?this.project.name:'Current state'

            this.insights = this.linqset.insights
            this.comparison = this.project?this.linqsetRepo.comparison:undefined
            this.headlines = this.createHeadlines(this.insights, this.comparison?.insights)
            
            this.loading = false
            
            if (this.linqset && this.gcComponent) {
                this.graphComponent = this.gcComponent.graphComponent;
            }

            // not awaited: the page is ready, and the choice comes whenever the user makes it
            this.onboardingService.organisationHomeLoaded(this.organisation, this.app.smallScreen).then(choice => {
                if (choice === ONBOARDING_CHOICE.CREATE) this.executeFunction(FUNCTION.CREATE_MODEL)
            })
        }catch(err) {
            console.log(err)
            this.router.navigateByUrl(DEFAULT_URL())
        }finally {
            this.loading = false
        }
    }

    isHelpOn() {
        return AppComponent.help
    }

    // a handful of headline numbers for the page header. Zero-valued optional metrics are left out to keep it focused. In a
    // project each is set beside the current state's, as the analysis overview does
    private createHeadlines(insights:ModelInsights, comparison?:ModelInsights):HeadlineMetric[] {
        if (!insights?.superTypes) return []

        const actionHours = (i:ModelInsights) => i.superTypes?.[NODE_SUPERTYPE.ACTION]?.hours||0
        const actions = (i:ModelInsights) => i.superTypes?.[NODE_SUPERTYPE.ACTION]?.number||0
        const automated = (i:ModelInsights) => actions(i)?Math.round(100*(i.actionTypes?.[ACTION_TYPE.AUTOMATED]?.number||0)/actions(i)):0
        const remaining = (i:ModelInsights) => (i.modelledCapacity||0) - (i.superTypes?.[NODE_SUPERTYPE.ROLE]?.hours||0)

        const count = (value:number) => ({value:this.formatter.formatNumber(value), short:this.formatter.formatAbbreviatedNumber(value)})
        const money = (value:number) => (value < 0 ? '-' : '') + this.formatter.currencySymbol + this.formatter.formatAbbreviatedNumber(Math.abs(value))
        const cost = (value:number) => ({value:this.formatter.formatCost(value), short:money(value)})
        const hours = (value:number) => { const h = count(Math.round(value)); return {value:`${h.value} h`, short:`${h.short} h`} }
        const percent = (value:number) => ({value:`${value}%`, short:`${value}%`})

        // the figure, and how it changed from the current state's
        const format = changeFormat(money, v => this.formatter.formatAbbreviatedNumber(v))
        const written = (m:Measure) => m.unit === 'percent' ? `${Math.round(m.amount)}%` : m.unit === 'money' ? money(m.amount) : m.unit === 'hours' ? `${this.formatter.formatAbbreviatedNumber(m.amount)} h` : this.formatter.formatAbbreviatedNumber(m.amount)
        const compared = (of:(i:ModelInsights) => number, better:Measure['better'], unit:Measure['unit']) => {
            if (!comparison) return {}
            const before = measure(of(comparison), better, unit)
            return {change:changeFrom(before, measure(of(insights), better, unit), format), before:written(before)}
        }

        const metrics:HeadlineMetric[] = [
            {label:'Models', ...count(this.linqset.models.length), tooltip:'Number of models in this workspace'},
            {label:'Actions', ...count(actions(insights)), tooltip:'Number of actions across all models', ...compared(actions, 'none', 'count')},
            {label:'Hours modelled', ...count(Math.round(actionHours(insights))), tooltip:'Annual hours to execute all modelled actions', ...compared(actionHours, 'down', 'hours')},
            {label:'Cost', ...cost(Math.round(getTotalModelCost(insights))), tooltip:'Estimated annual cost of modelled work', ...compared(getTotalModelCost, 'down', 'money')},
        ]

        if (insights.modelledCapacity || comparison?.modelledCapacity) {
            metrics.push({label:'Capacity remaining', ...hours(remaining(insights)), tooltip:'Role capacity left after modelled work', warn:remaining(insights) < 0, ...compared(remaining, 'up', 'hours')})
        }

        if (actions(insights)) {
            metrics.push({label:'Automated', ...percent(automated(insights)), tooltip:'Proportion of actions performed by systems only', ...compared(automated, 'up', 'percent')})
        }

        if (insights.outputRevenue || comparison?.outputRevenue) {
            metrics.push({label:'Revenue', ...cost(Math.round(insights.outputRevenue||0)), tooltip:'Annual revenue from model outputs', ...compared(i => i.outputRevenue||0, 'up', 'money')})
        }

        return metrics
    }

    // the walkthrough of this organisation's models, comparing with the project being worked in, if any
    openModelWalkthrough() {
        this.router.navigateByUrl(MODEL_WALKTHROUGH_URL(this.organisation._key, this.project?._key))
    }

    openOrganisationModel() {
        this.router.navigateByUrl(SKETCH_URL(this.organisation._key, ORGANISATION_MODEL_KEY, MODEL_TYPE.MODELS, (this.project && this.project._key)))
    }

    openCarousel() {
        this.insightsComponent.openCarousel()
    }

    openPeopleSystemsModel() {
        this.router.navigateByUrl(SKETCH_URL(this.organisation._key, ORGANISATION_MODEL_KEY, MODEL_TYPE.PEOPLE_AND_SYSTEMS, (this.project && this.project._key)))
    }

    hasAdminAccess() {
        return this.project?(this.project.role && this.project.role === USER_ROLE.ADMIN):(this.organisation && this.organisation.role && this.organisation.role === USER_ROLE.ADMIN)
    }

    hasWriteAccess() {
        return this.project?(this.project.role && this.project.role <= USER_ROLE.WRITE):(this.organisation && this.organisation.role && this.organisation.role <= USER_ROLE.WRITE)
    }
    
    getSelectedItem() {
        this.selectedItem = this.graphComponent.selection.last()
        if (!this.selectedItem) return null

        if (!this.selectedItem.tag) {
            this.selectedItem = null
        }
        return this.selectedItem
    }

    modelBackupCount() {
        return this.organisation?.models?.reduce((n, m) => n + (m.backups?.length || 0), 0) || 0
    }

    backupCount() {
        return this.modelBackupCount() + (this.organisation?.backup ? 1 : 0)
    }

    createOrganisationBackup() {
        const message = this.organisation.backup
            ? `This replaces the organisation backup taken on ${formatDate(this.organisation.backup.date, "d MMM yyyy", this.locale)} with a new copy of all model data. The old backup can't be recovered.`
            : 'This makes a copy of all model data for the organisation. Backed-up nodes count towards your total nodes, and so your bill.'

        const dialogData = new ConfirmDialogModel(this.organisation.backup ? "Replace organisation backup" : "Create organisation backup", [message], "create backup", false, "Back up", "Cancel");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "450px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult?.button === "Back up") {
                this.loading = true
                try {
                    await this.backupService.createOrganisationBackup(this.organisation, "Organisation backup")
                    this.loadOrganisationData()
                }finally {
                    this.loading = false
                }
            }
        })
    }

    async restoreOrganisationBackup() {
        const {backup} = this.organisation
        if (!backup) return

        // check the stored backup still matches what was saved before offering to restore it
        const count = await this.backupService.getOrganisationBackupCount(this.organisation)
        const messages = [`The organisation backup was taken on ${formatDate(backup.date, "d MMM yyyy, h:mm a", this.locale)} and contains ${count.nodeCount} nodes and ${count.edgeCount} edges.`]
        if (count.nodeCount !== backup.nodeCount || count.edgeCount !== backup.edgeCount) {
            messages.push(`Warning: when it was taken it had ${backup.nodeCount} nodes and ${backup.edgeCount} edges. The backup may be incomplete.`)
        }
        messages.push("Restoring replaces all current model data with the backup. This can't be undone.")

        const dialogData = new ConfirmDialogModel("Restore organisation backup", messages, "restore backup", false, "Restore", "Cancel");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "450px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult?.button === "Restore") {
                this.loading = true
                try {
                    await this.backupService.restoreOrganisationBackup(this.organisation)
                    this.loadOrganisationData()
                }finally {
                    this.loading = false
                }
            }
        })
    }

    isSysAdmin() {
        return this.userService.isSysAdmin()
    }

    // nothing in it yet, so an organisation export can be imported into it (the server checks there are no nodes either)
    isEmpty() {
        return !this.organisation?.models?.length && !this.organisation?.projects?.length
    }

    async exportOrganisation() {
        this.loading = true
        try {
            await this.transferService.exportOrganisation(this.organisation)
        }finally {
            this.loading = false
        }
    }

    async importIntoOrganisation() {
        if (await this.transferService.importIntoOrganisation(this.organisation, busy => this.loading = busy)) this.loadOrganisationData()
    }

    async deleteAllBackups() {
        const count = this.modelBackupCount()
        const message = `Permanently delete all ${count} model backup${count === 1 ? '' : 's'}? The organisation backup is kept. This can't be undone.`;
        const dialogData = new ConfirmDialogModel("Delete all model backups", [message], "delete all", false, "Delete all", "Cancel");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "450px",
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult?.button === "Delete all") {
                await this.backupService.deleteAllBackups(this.organisation)
                this.loadOrganisationData()
            }
        })
    }

    getSelectedEdge() {
        return this.selectedItem as IEdge
    }

    getSelectedNode() {
        return this.selectedItem as INode
    }

    graphItemDoubleClickHandler(item:IModelItem) {
        if (item instanceof INode) {
            if (item.tag?.model) this.modelService.editModel(this.organisation, item.tag.model, this.projectKey)
        }
    }

    updateProjectDetails() {
        if (this.project) this.functionService.executeFunction(FUNCTION.EDIT_PROJECT, undefined, undefined, this.project)
    }

    async executeFunction(fn:FUNCTION, value?:any) {

        try {
            this.loading = true
            await this.functionService.executeFunction(fn, value, this.linqset, this.project, this.graphComponent, this.gcComponent)
        }catch(err) {
            this.loading = false
            console.error(err)
        }finally {
            this.loading = false
        }
    }

    zoomToModelItem() {
        this.graphService.zoomToModelItem(this.graphComponent)
    }

    handleRefreshEvent(event) {
        this.loadOrganisationData()
    }

    handleSelectNodeEvent(event) {
        const nodes = this.graphComponent.graph.nodes.filter(n => n.tag && n.tag.node && n.tag.node._key === event)
        if (nodes) this.graphService.selectModelItems(this.graphComponent, nodes.toArray())
    }

    public async handleFunctionEvent(event:{function:FUNCTION, value?:any}) {
        this.executeFunction(event.function, event.value)
    }

    handleModelFilterEvent(event:{functions:MODEL_FUNCTION[], tags:string[]}) {
        // holding off this at momement. may implement in future
    }
    
    inviteUser() {
        this.openUserDialog()
    }

    private async openUserDialog() {
        const data:UserDialogData = {user:{} as User, isInvite:true, numExistingUsers:this.organisationService.users?.length || 0, exempt:this.organisation.isExemptFromPayment}
        const dialogRef = this.dialog.open(UserDialogComponent, {data, width:'520px', maxWidth:'95vw', hasBackdrop:true});

        dialogRef.afterClosed().subscribe(async (result:User|undefined) => {
            if (!result) return
            try {
                await this.userService.inviteUser(this.organisation, result)
            }catch(err) {
                return      // the error has already been shown
            }
            this.organisationService.users = await this.organisationService.getUsersForOrganisation(this.organisation)
        })
    }

    // changes the project's models from a description the user gives AI, then shows the project as changed
    changeWithAI() {
        if (!this.project) return
        ModelChangeDialogComponent.open(this.dialog, {organisation:this.organisation, project:this.project}).afterClosed().subscribe(changed => {
            if (changed) this.loadOrganisationData()
        })
    }

    async confirmMergeDialog() {
        let message = `This will push all of this projects changes (all models) into current state. It cannot be undone. Are you sure?`;
        const dialogData = new ConfirmDialogModel("Confirm Action", [message], "replace");

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: "500px",
            data: dialogData
        });
        
        dialogRef.afterClosed().subscribe(async dialogResult => {
            if (dialogResult) {
                try {
                    this.loading = true
                    await this.linqsetRepo.mergeProjectWithCurrentState(this.organisation._key, this.project._key)
                    this.loadOrganisationData()
                }finally {
                    this.loading = false
                }
            }
        });
    }
}